import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { FaEdit, FaArrowLeft, FaChevronLeft, FaChevronRight, FaLayerGroup, FaArrowRight } from "react-icons/fa";

import { fetchChapterById, fetchChaptersForCourse, fetchTopicById, fetchTopicsForChapter } from "../../../apis/CourseApis";
import { fetchCourseAccess } from "../../../apis/AccessApis";
import { useAuth } from "../../Authentication/AuthContext";
import { useToastHelpers } from "../../Toasts/ToastUtils";
import SeoMeta from "../../SeoMeta";
import { SkeletonTopicView } from "../../Skeletons";
import { FREE_CHAPTER_COUNT, getChapterUnlockStatus } from "../../../config/courseUnlock";
import { computeChapterCompletionPercent } from "../../../utils/courseProgress";
import { useTopicCache } from "./TopicCacheContext";

import TheoryView from "./views/TheoryView";
import VideoView from "./views/VideoView";
import MCQView from "./views/MCQView";
import CodingView from "./views/CodingView";

const TopicView = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { errorToast } = useToastHelpers();
  const { user, loggedInUser } = useAuth();

  // Get filter from URL query param (e.g., ?filter=theory)
  const activeFilter = searchParams.get("filter") || "all";

  // Use topic cache (always call the hook - it's safe because TopicCacheProvider is in App.js)
  const topicCache = useTopicCache();

  const [topic, setTopic] = useState(null);
  const [chapterTopics, setChapterTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lockState, setLockState] = useState({ checked: false, locked: false, reason: null });
  const [chapterInfo, setChapterInfo] = useState(null); // { id, title, courseId }
  const [nextChapter, setNextChapter] = useState(null); // { id, title } or null

  // Filter chapter topics based on active filter
  // If filter is set but current topic doesn't match, we still include current topic's type in navigation
  const filteredChapterTopics = useMemo(() => {
    if (activeFilter === "all") return chapterTopics;

    const filtered = chapterTopics.filter((t) => t.type === activeFilter);

    // Edge case: if current topic doesn't match filter (e.g., direct URL with wrong filter)
    // Reset to showing all topics for proper navigation
    if (topic && filtered.length > 0 && !filtered.some((t) => t.id === topicId)) {
      // Current topic not in filtered list - return all topics instead
      return chapterTopics;
    }

    return filtered;
  }, [chapterTopics, activeFilter, topic, topicId]);

  // Current index within filtered list (for navigation)
  const filteredCurrentIndex = useMemo(() => {
    if (!topic || filteredChapterTopics.length === 0) return -1;
    const idx = filteredChapterTopics.findIndex((t) => t.id === topicId);
    return idx;
  }, [filteredChapterTopics, topicId, topic]);

  // Determine effective filter for navigation (handles edge cases)
  const effectiveFilter = useMemo(() => {
    if (activeFilter === "all") return "all";
    // If current topic is in filtered list, use the filter; otherwise reset to "all"
    if (topic && filteredChapterTopics.some((t) => t.id === topicId)) {
      return activeFilter;
    }
    return "all";
  }, [activeFilter, topic, filteredChapterTopics, topicId]);

  // Memoized fetch function
  const fetchTopicData = useCallback(async (id) => {
    return topicCache ? topicCache.fetchTopic(id) : fetchTopicById(id);
  }, [topicCache]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        // Check cache first for instant load
        const cached = topicCache?.getFromCache(topicId);
        if (cached) {
          setTopic(cached);
          // Don't set loading false yet - still need to fetch chapter topics
        } else {
          setLoading(true);
        }

        // Fetch topic (will use cache internally if available)
        const t = await fetchTopicData(topicId);
        if (!mounted) return;
        setTopic(t);

        if (t.chapterId) {
          // Fetch chapter topics
          const res = await fetchTopicsForChapter(t.chapterId);
          if (!mounted) return;

          const list = Array.isArray(res?.topics)
            ? res.topics
            : Array.isArray(res) ? res : [];

          setChapterTopics(list);

          // Fetch chapter info for "Back to Chapter" link
          try {
            const chapter = await fetchChapterById(t.chapterId);
            if (!mounted) return;

            const courseId = String(chapter?.courseId || chapter?.course || "");
            setChapterInfo({
              id: t.chapterId,
              title: chapter?.title || "Chapter",
              courseId,
            });

            // Fetch next chapter info
            if (courseId) {
              const chaptersRes = await fetchChaptersForCourse(courseId);
              const chaptersList = Array.isArray(chaptersRes?.chapters)
                ? chaptersRes.chapters
                : Array.isArray(chaptersRes) ? chaptersRes : [];

              const currentChapterIndex = chaptersList.findIndex(
                (c) => String(c?.id || c?._id || "") === String(t.chapterId)
              );

              if (currentChapterIndex >= 0 && currentChapterIndex < chaptersList.length - 1) {
                const next = chaptersList[currentChapterIndex + 1];
                if (mounted) {
                  setNextChapter({
                    id: next?.id || next?._id,
                    title: next?.title || "Next Chapter",
                  });
                }
              } else {
                if (mounted) setNextChapter(null);
              }
            }
          } catch {
            // Non-critical - chapter info fetch failed
            if (mounted) setChapterInfo({ id: t.chapterId, title: "Chapter", courseId: "" });
          }
        }
      } catch {
        errorToast("Failed to load topic");
        navigate("/courses");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [topicId, fetchTopicData, errorToast, navigate, topicCache]);

  // Prefetch adjacent topics when current topic and filtered list are ready
  useEffect(() => {
    if (!topic || !topicCache || filteredChapterTopics.length === 0 || filteredCurrentIndex < 0) return;

    const topicsToPrefetch = [];

    // Prefetch previous topic in filtered list
    if (filteredCurrentIndex > 0) {
      topicsToPrefetch.push(filteredChapterTopics[filteredCurrentIndex - 1].id);
    }

    // Prefetch next topic in filtered list
    if (filteredCurrentIndex < filteredChapterTopics.length - 1) {
      topicsToPrefetch.push(filteredChapterTopics[filteredCurrentIndex + 1].id);
    }

    if (topicsToPrefetch.length > 0) {
      topicCache.prefetchTopics(topicsToPrefetch);
    }
  }, [topic, filteredChapterTopics, filteredCurrentIndex, topicCache]);

  // Chapter-level access check (paid users unlocked, free users progressive unlock, rest locked)
  useEffect(() => {
    let mounted = true;

    const checkAccess = async () => {
      if (!topic?.chapterId) {
        if (mounted) setLockState({ checked: true, locked: false, reason: null });
        return;
      }

      const isAdmin = String(loggedInUser?.role || "").toLowerCase() === "admin";
      if (isAdmin) {
        if (mounted) setLockState({ checked: true, locked: false, reason: null });
        return;
      }

      try {
        const chapter = await fetchChapterById(topic.chapterId);
        const courseId = String(chapter?.courseId || chapter?.course || "");
        if (!courseId) {
          if (mounted) setLockState({ checked: true, locked: false, reason: null });
          return;
        }

        const chaptersRes = await fetchChaptersForCourse(courseId);
        const chaptersList = Array.isArray(chaptersRes?.chapters)
          ? chaptersRes.chapters
          : Array.isArray(chaptersRes)
            ? chaptersRes
            : [];

        const chapterIndex = Math.max(
          0,
          chaptersList.findIndex((c) => String(c?.id || c?._id || "") === String(topic.chapterId))
        );

        let allowed = false;
        if (user?.token) {
          const userId = user?.userId || loggedInUser?.id || loggedInUser?._id;
          if (userId) {
            try {
              const access = await fetchCourseAccess({
                courseId,
                userId,
                token: user.token,
              });
              allowed = Boolean(access?.allowed);
            } catch {
              allowed = false;
            }
          }
        }

        const chapterCompletionPercents = [];
        if (!allowed && user?.token && chapterIndex > 0 && chapterIndex < FREE_CHAPTER_COUNT) {
          const prev = chaptersList[chapterIndex - 1];
          const prevChapterId = prev?.id || prev?._id;
          const prevPercent = await computeChapterCompletionPercent({
            chapterId: prevChapterId,
            token: user.token,
          }).catch(() => 0);
          chapterCompletionPercents[chapterIndex - 1] = prevPercent;
        }

        const unlock = getChapterUnlockStatus({
          chapterIndex,
          isPaidUser: allowed,
          isAdmin: false,
          chapterCompletionPercents,
        });

        if (!mounted) return;
        setLockState({ checked: true, locked: !unlock.unlocked, reason: unlock.reason });
      } catch (err) {
        console.error("Failed to check topic access:", err);
        if (mounted) setLockState({ checked: true, locked: false, reason: null });
      }
    };

    checkAccess();
    return () => {
      mounted = false;
    };
  }, [topic?.chapterId, user?.token, user?.userId, loggedInUser?.id, loggedInUser?._id, loggedInUser?.role]);

  if (loading) {
    return <SkeletonTopicView />;
  }

  if (!topic) return null;

  if (lockState.checked && lockState.locked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="ca-card p-8 max-w-lg w-full text-center">
          <div className="text-4xl mb-3">🔒</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Content Locked</h2>
          <p className="text-gray-600 mb-6">{lockState.reason || "This content is locked."}</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="ca-btn-primary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const handlePrevious = () => {
    if (filteredCurrentIndex > 0) {
      const prevTopicId = filteredChapterTopics[filteredCurrentIndex - 1].id;
      const filterParam = effectiveFilter !== "all" ? `?filter=${effectiveFilter}` : "";
      navigate(`/topic/${prevTopicId}${filterParam}`);
    }
  };

  const handleNext = () => {
    if (filteredCurrentIndex < filteredChapterTopics.length - 1) {
      const nextTopicId = filteredChapterTopics[filteredCurrentIndex + 1].id;
      const filterParam = effectiveFilter !== "all" ? `?filter=${effectiveFilter}` : "";
      navigate(`/topic/${nextTopicId}${filterParam}`);
    }
  };

  const handleBackToChapter = () => {
    if (chapterInfo?.id) {
      navigate(`/chapter/${chapterInfo.id}`);
    } else if (topic?.chapterId) {
      navigate(`/chapter/${topic.chapterId}`);
    } else {
      navigate(-1);
    }
  };

  const handleNextChapter = () => {
    if (nextChapter?.id) {
      navigate(`/chapter/${nextChapter.id}`);
    }
  };

  // Check if navigation is possible
  const canGoPrevious = filteredCurrentIndex > 0;
  const canGoNext = filteredCurrentIndex >= 0 && filteredCurrentIndex < filteredChapterTopics.length - 1;
  const isLastTopic = filteredCurrentIndex >= 0 && filteredCurrentIndex === filteredChapterTopics.length - 1;
  const totalInFilter = filteredChapterTopics.length;
  const currentPosition = filteredCurrentIndex >= 0 ? filteredCurrentIndex + 1 : 1;

  // Navigation Footer Component (reusable for both views)
  const NavigationFooter = () => (
    <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 flex items-center justify-between gap-4">
          {/* Left Section - Back to Chapter */}
          <button
            onClick={handleBackToChapter}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 text-sm font-medium group"
          >
            <FaArrowLeft className="text-xs group-hover:-translate-x-0.5 transition-transform" />
            <span className="hidden sm:inline">Back to Chapter</span>
            <span className="sm:hidden">Back</span>
          </button>

          {/* Center Section - Navigation Controls */}
          <div className="flex items-center gap-2">
            {/* Previous Button */}
            <button
              disabled={!canGoPrevious}
              onClick={handlePrevious}
              className={`
                flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                ${canGoPrevious
                  ? "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                  : "text-gray-300 cursor-not-allowed"
                }
              `}
            >
              <FaChevronLeft className="text-xs" />
              <span className="hidden sm:inline">Previous</span>
            </button>

            {/* Progress Indicator */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
              <FaLayerGroup className="text-gray-400 text-xs" />
              <span className="text-sm font-semibold text-gray-700">
                {currentPosition} / {totalInFilter}
              </span>
              {effectiveFilter !== "all" && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium capitalize">
                  {effectiveFilter}
                </span>
              )}
            </div>

            {/* Next Button or Next Chapter */}
            {canGoNext ? (
              <button
                onClick={handleNext}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200"
              >
                <span className="hidden sm:inline">Next</span>
                <FaChevronRight className="text-xs" />
              </button>
            ) : isLastTopic && nextChapter ? (
              <button
                onClick={handleNextChapter}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md hover:from-green-600 hover:to-emerald-700 transition-all duration-200 group"
              >
                <span className="hidden sm:inline">Next Chapter</span>
                <span className="sm:hidden">Next Ch.</span>
                <FaArrowRight className="text-xs group-hover:translate-x-0.5 transition-transform" />
              </button>
            ) : (
              <button
                disabled
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-300 cursor-not-allowed"
              >
                <span className="hidden sm:inline">Next</span>
                <FaChevronRight className="text-xs" />
              </button>
            )}
          </div>

          {/* Right Section - Chapter Progress or Empty for balance */}
          <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500">
            {chapterInfo?.title && (
              <span className="max-w-[150px] truncate" title={chapterInfo.title}>
                {chapterInfo.title}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <SeoMeta title={topic.title} description={topic.description} />

      {["coding", "mcq"].includes(topic.type) ? (
        // Coding and MCQ view
        <div className="bg-white">
          <div className="min-h-screen relative">
            {/* Admin Edit Button */}
            {String(loggedInUser?.role || "").toLowerCase() === "admin" && (
              <button
                onClick={() => navigate(`/topics/edit/${topic.id}`)}
                className="absolute top-4 right-40 z-10 flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-md"
                title="Edit Topic"
              >
                <FaEdit size={14} />
                Edit
              </button>
            )}
            {topic.type === "coding" && <CodingView topic={topic} />}
            {topic.type === "mcq" && <MCQView topic={topic} />}
          </div>

          {/* Navigation Footer */}
          <NavigationFooter />
        </div>
      ) : topic.type === "theory" || topic.type === "video" ? (
        // Theory and video view - 20/80 split on desktop, stacked on mobile
        <div className="bg-white">
          <div className="flex flex-col md:flex-row bg-gray-50 md:h-screen md:overflow-hidden">
            {/* ===== LEFT PANEL (20%) - Title, Info & Description ===== */}
            <div className="w-full md:w-1/5 bg-white border-b md:border-b-0 md:border-r border-gray-300 overflow-y-auto p-6 md:h-screen">
              {/* Back to Chapter Button */}
              <button
                onClick={handleBackToChapter}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium mb-4 group"
              >
                <FaArrowLeft className="text-xs group-hover:-translate-x-0.5 transition-transform" />
                <span>Back to Chapter</span>
              </button>

              {/* Title */}
              <h2 className="text-lg font-bold text-gray-900 mb-3">
                {topic.title}
              </h2>

              {/* Badge */}
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium text-xs">
                  {topic.type === "theory" ? "Theory" : "Video"}
                </span>
              </div>

              {/* Admin Edit Button */}
              {String(loggedInUser?.role || "").toLowerCase() === "admin" && (
                <button
                  onClick={() => navigate(`/topics/edit/${topic.id}`)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium mb-4"
                  title="Edit Topic"
                >
                  <FaEdit size={14} />
                  Edit Topic
                </button>
              )}

              {/* Divider */}
              <div className="border-t border-gray-200 my-5"></div>

              {/* Description */}
              {topic.description && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 text-xs uppercase tracking-wide text-gray-600 mb-2">
                    About
                  </h4>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {topic.description}
                  </p>
                </div>
              )}

              {/* Learning Objectives */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 text-xs uppercase tracking-wide text-gray-600 mb-3">
                  What you'll learn
                </h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2.5 text-sm text-gray-700">
                    <span className="text-green-600 font-bold mt-0.5 flex-shrink-0">✓</span>
                    <span>Core concepts and fundamentals</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-gray-700">
                    <span className="text-green-600 font-bold mt-0.5 flex-shrink-0">✓</span>
                    <span>Practical implementation techniques</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-gray-700">
                    <span className="text-green-600 font-bold mt-0.5 flex-shrink-0">✓</span>
                    <span>Real-world use cases and examples</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-gray-700">
                    <span className="text-green-600 font-bold mt-0.5 flex-shrink-0">✓</span>
                    <span>Industry best practices</span>
                  </li>
                </ul>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 my-5"></div>

              {/* Meta Info */}
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Difficulty
                  </p>
                  <p className="text-sm font-semibold text-green-700 bg-green-50 px-3 py-1.5 rounded-md inline-block">
                    Beginner
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Duration
                  </p>
                  <p className="text-sm font-semibold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-md inline-block">
                    20-30 minutes
                  </p>
                </div>
              </div>
            </div>

            {/* ===== RIGHT PANEL (80%) - Content ===== */}
            <div className="w-full md:w-4/5 bg-white overflow-y-auto p-6 md:h-screen">
              {topic.type === "theory" && <TheoryView topic={topic} />}
              {topic.type === "video" && <VideoView topic={topic} />}
            </div>
          </div>

          {/* Navigation Footer */}
          <NavigationFooter />
        </div>
      ) : null}
    </>
  );
};

export default TopicView;
